'use client';

import React from 'react';
import { FieldLabel } from '../field-label';
import { cn } from '../../lib/utils';

export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: boolean;
}

export interface FieldProps {
  id?: string;
  label: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  className?: string;
  /** Apply these attributes to the actual control, including custom selects. */
  children: (props: FieldControlProps) => React.ReactNode;
}

/** A label, control and linked messages; validation stays with the caller. */
export function Field({
  id,
  label,
  hint,
  error,
  required,
  optional,
  className,
  children,
}: FieldProps) {
  const generatedId = React.useId();
  const controlId = id ?? `field-${generatedId}`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const hasHint = Boolean(hint);
  const hasError = Boolean(error);
  return (
    <div
      className={cn('w-full max-w-[30rem] min-w-0 break-words [overflow-wrap:anywhere]', className)}
      data-field
    >
      <FieldLabel htmlFor={controlId} required={required} optional={optional}>
        {label}
      </FieldLabel>
      {children({
        id: controlId,
        'aria-required': required || undefined,
        'aria-describedby':
          [hasHint ? hintId : undefined, hasError ? errorId : undefined]
            .filter(Boolean)
            .join(' ') || undefined,
        'aria-invalid': hasError ? true : undefined,
      })}
      {hasHint && (
        <p id={hintId} className="mt-1 text-caption text-text-muted">
          {hint}
        </p>
      )}
      {hasError && (
        <p id={errorId} className="mt-1 text-caption text-status-danger-fg">
          {error}
        </p>
      )}
    </div>
  );
}
